import { invoke } from "~/runtime"
import { useSignal } from "~/sdk/signal"
import type { JSX } from "react"
import type { Error } from "~/actions/contact"

export default function Contact() {
    const result = useSignal<{
        success: boolean
        errors: Error[]
    }>({
        success: false,
        errors: [],
    })

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const form = event.currentTarget
        const formData = new FormData(form)
        const data = Object.fromEntries(formData)
        const { errors } = await invoke['site'].actions.contact(data)

        if (errors?.length) {
            result.value = { success: false, errors }
            return
        }

        result.value = { success: true, errors: [] }
    }

    const isInvalid = (inputName: string) =>
        result.value.errors.some((error) => error.field === inputName)

    const getErrorMessage = (inputName: string) =>
        result.value.errors.find((error) => error.field === inputName)?.message

    const removeErrorOnInput = (
        event: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >,
    ) => {
        const inputName = event.currentTarget.name

        result.value = {
            ...result.value,
            errors: result.value.errors.filter((error) => error.field !== inputName),
        }
    }

    if (result.value.success) {
        return (
            <div className='w-full max-w-5xl p-6 mt-8 bg-gray-100 grid gap-3 lg:p-12 lg:my-8'>
                <p className='text-black text-2xl font-bold leading-snug'>
                    Mensagem enviada com sucesso!
                    <br />
                    Em breve retornaremos o contato.
                </p>
            </div>
        )
    }

    return (
        <form
            className='w-full max-w-5xl p-6 mt-8 bg-gray-100 grid gap-3 lg:p-12 lg:my-8'
            onSubmit={onSubmit}
        >
            <div className='grid gap-1.5 md:relative'>
                <label className='text-sm font-bold' htmlFor='name'>
                    Nome
                </label>

                <input
                    id='name'
                    className='px-5 py-3.5 border border-[#d3d3d3] text-[#8b8888]'
                    name='nome'
                    type='text'
                    placeholder='Nome'
                    onChange={removeErrorOnInput}
                />

                {isInvalid('nome') && (
                    <span className='text-sm text-red-500 font-medium md:absolute md:right-0'>
                        {getErrorMessage('nome')}
                    </span>
                )}
            </div>

            <div className='grid gap-1.5 md:relative'>
                <label className='text-sm font-bold' htmlFor='phone'>
                    Telefone (com DDD)
                </label>

                <input
                    id='phone'
                    className='px-5 py-3.5 border border-[#d3d3d3] text-[#8b8888]'
                    name='telefone'
                    type='tel'
                    placeholder='Telefone'
                    onChange={(event) => {
                        const value = event.currentTarget.value

                        event.currentTarget.value = value
                            .replace(/\D/g, '')
                            .replace(/^(\d)/, '($1')
                            .replace(/^(\(\d{2})(\d)/, '$1) $2')
                            .replace(/(\d{4,5})(\d{1,4})/, '$1-$2')
                            .replace(/(-\d{4})\d+?$/, '$1')

                        removeErrorOnInput(event)
                    }}
                />

                {isInvalid('telefone') && (
                    <span className='text-sm text-red-500 font-medium md:absolute md:right-0'>
                        {getErrorMessage('telefone')}
                    </span>
                )}
            </div>

            <div className='grid gap-1.5 md:relative'>
                <label className='text-sm font-bold' htmlFor='email'>
                    E-mail
                </label>

                <input
                    id='email'
                    className='px-5 py-3.5 border border-[#d3d3d3] text-[#8b8888]'
                    name='email'
                    type='email'
                    placeholder='E-mail'
                    onChange={removeErrorOnInput}
                />

                {isInvalid('email') && (
                    <span className='text-sm text-red-500 font-medium md:absolute md:right-0'>
                        {getErrorMessage('email')}
                    </span>
                )}
            </div>

            <div className='grid gap-1.5 md:relative'>
                <label className='text-sm font-bold' htmlFor='order'>
                    Número do pedido
                </label>

                <input
                    id='order'
                    className='px-5 py-3.5 border border-[#d3d3d3] text-[#8b8888]'
                    name='pedido'
                    type='text'
                    placeholder='Número do pedido'
                />
            </div>

            <div className='grid gap-1.5 md:relative'>
                <label className='text-sm font-bold' htmlFor='subject'>
                    Assunto
                </label>

                <select
                    id='subject'
                    className='px-5 py-3.5 border border-[#d3d3d3] text-[#8b8888]'
                    name='assunto'
                    onChange={removeErrorOnInput}
                >
                    <option value='' label='Selecione' disabled selected>
                        Selecione
                    </option>
                    <option value='Consultar pedido'>Consultar pedido</option>
                    <option value='Garantia de melhor preço'>Garantia de melhor preço</option>
                    <option value='Troca & Devolução'>Troca & Devolução</option>
                    <option value='Promoções'>Promoções</option>
                    <option value='Revenda'>Revenda</option>
                    <option value='Segunda via de Nota Fiscal'>Segunda via de Nota Fiscal</option>
                </select>

                {isInvalid('assunto') && (
                    <span className='text-sm text-red-500 font-medium md:absolute md:right-0'>
                        {getErrorMessage('assunto')}
                    </span>
                )}
            </div>

            <div className='grid gap-1.5 md:relative'>
                <label className='text-sm font-bold' htmlFor='message'>
                    Mensagem
                </label>

                <textarea
                    id='message'
                    className='px-5 py-3.5 border border-[#d3d3d3] text-[#8b8888] resize-y'
                    name='mensagem'
                    placeholder='Deixe a sua mensagem...'
                    onChange={removeErrorOnInput}
                />

                {isInvalid('mensagem') && (
                    <span className='text-sm text-red-500 font-medium md:absolute md:right-0'>
                        {getErrorMessage('mensagem')}
                    </span>
                )}
            </div>

            <button className='h-12 bg-salmon text-white text-lg font-bold uppercase transition-opacity hover:opacity-80'>
                Enviar
            </button>
        </form>
    )
}
